import { Link } from "react-router-dom";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Button } from "../ui/button"
import { Card, CardContent } from "../ui/card"
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Users,
  Award,
  Clock,
  Heart,
  Wind,
} from "lucide-react"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion"

export default function PulmonologyPage() {
  const { open: openAppointment } = useAppointment();
  const doctors = useDepartmentDoctors("Respiratory Medicine");


  const subspecialties = [
    {
      title: "Respiratory Medicine",
      description:
        "Diagnosis and treatment of common respiratory conditions including asthma, bronchitis, and pneumonia.",
      image: "/respiratory-medicine-care.jpg",
    },
    {
      title: "Interventional Pulmonology",
      description:
        "Advanced minimally invasive procedures for lung diseases using bronchoscopy and thoracoscopy.",
      image: "/interventional-pulmonology-care.jpg",
    },
    {
      title: "Sleep Medicine",
      description:
        "Diagnosis and treatment of sleep disorders including sleep apnea, insomnia, and narcolepsy.",
      image: "/sleep-medicine-pulmonology.jpg",
    },
    {
      title: "Critical Care Pulmonology",
      description:
        "Intensive care for patients with severe respiratory failure requiring ventilator support.",
      image: "/critical-care-pulmonology.jpg",
    },
    {
      title: "Allergic & Occupational Lung Diseases",
      description:
        "Treatment for allergic rhinitis, occupational asthma, and environmental lung conditions.",
      image: "/allergic-lung-diseases.jpg",
    },
    {
      title: "Tuberculosis & Infectious Diseases",
      description:
        "Specialized care for TB, COVID-19 complications, and other respiratory infections.",
      image: "/tuberculosis-treatment-care.jpg",
    },
  ]

  const treatments = [
    "Asthma Management & Treatment",
    "COPD (Chronic Obstructive Pulmonary Disease) Care",
    "Bronchoscopy & Interventional Procedures",
    "Sleep Study & Sleep Apnea Treatment",
    "Pulmonary Function Testing (PFT)",
    "Tuberculosis Diagnosis & Treatment",
    "Interstitial Lung Disease Management",
    "Lung Cancer Screening & Evaluation",
    "Pulmonary Rehabilitation Programs",
    "Ventilator Management & Weaning",
    "Pleural Procedures (Thoracentesis, Chest Tube)",
    "Allergy Testing & Immunotherapy",
  ]

  const whyChoose = [
    {
      icon: Users,
      title: "Expert Pulmonologists",
      description:
        "Experienced respiratory specialists with expertise in complex lung conditions",
    },
    {
      icon: Award,
      title: "Advanced Diagnostics",
      description:
        "State-of-the-art pulmonary function testing and imaging facilities",
    },
    {
      icon: Wind,
      title: "Comprehensive Care",
      description:
        "Complete respiratory care from diagnosis to rehabilitation",
    },
    {
      icon: Clock,
      title: "24x7 Emergency Care",
      description:
        "Round-the-clock care for respiratory emergencies and critical cases",
    },
    {
      icon: Heart,
      title: "Sleep Lab Facility",
      description:
        "Dedicated sleep study lab for accurate diagnosis of sleep disorders",
    },
  ]

  const articles = [
    {
      title: "Understanding Asthma: Triggers, Symptoms & Modern Treatment",
      excerpt:
        "Learn about asthma triggers, recognizing symptoms, and advanced treatment options available today.",
    },
    {
      title: "COPD Management: Living Better with Chronic Lung Disease",
      excerpt:
        "Comprehensive guide to managing COPD through medication, lifestyle changes, and pulmonary rehabilitation.",
    },
    {
      title: "Sleep Apnea: Signs You Shouldn't Ignore",
      excerpt:
        "Discover the warning signs of sleep apnea and why timely treatment is crucial for your health.",
    },
    {
      title: "Protecting Your Lungs: Prevention Tips for Respiratory Health",
      excerpt:
        "Essential tips for maintaining healthy lungs and preventing respiratory diseases.",
    },
  ]

  const faqs = [
    {
      question: "What conditions does the Pulmonology department treat?",
      answer: "Our Pulmonology department treats a wide range of respiratory conditions including asthma, COPD, pneumonia, tuberculosis, lung cancer, interstitial lung diseases, sleep apnea, bronchiectasis, pulmonary fibrosis, and occupational lung diseases. We also provide critical care for patients with respiratory failure.",
    },
    {
      question: "What is a Pulmonary Function Test (PFT)?",
      answer: "A Pulmonary Function Test (PFT) is a non-invasive test that measures how well your lungs work. It assesses lung capacity, airflow, and gas exchange. This test helps diagnose conditions like asthma, COPD, and restrictive lung diseases, and monitors the progression of lung diseases and effectiveness of treatment.",
    },
    {
      question: "Do you offer sleep studies for sleep apnea diagnosis?",
      answer: "Yes, we have a dedicated sleep lab facility that conducts comprehensive sleep studies (polysomnography) to diagnose sleep disorders including obstructive sleep apnea, central sleep apnea, insomnia, and other sleep-related conditions. Our sleep specialists then develop personalized treatment plans.",
    },
  ];
  const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals Pulmonology Department",
    "url": "https://mamshospitals.com/pulmonology",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/pulmonology-hospital-care.jpg",
    "description": "MAMS Hospitals provides advanced pulmonology treatment in Hyderabad including asthma treatment, COPD care, bronchoscopy, sleep apnea treatment, tuberculosis care, pulmonary rehabilitation, and critical respiratory care.",
    "medicalSpecialty": "Pulmonology",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "Asthma & COPD Management"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Bronchoscopy & Interventional Pulmonology"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Sleep Study & Sleep Apnea Treatment"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Pulmonary Function Testing (PFT)"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Tuberculosis & Lung Infection Treatment"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What conditions does the Pulmonology department treat?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "MAMS Hospitals treats asthma, COPD, pneumonia, tuberculosis, sleep apnea, lung infections, pulmonary fibrosis, and respiratory failure conditions."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide sleep studies for sleep apnea?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals provides sleep studies and advanced treatment for sleep apnea and sleep-related breathing disorders."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide bronchoscopy procedures?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, advanced bronchoscopy and interventional pulmonology procedures are available for diagnosis and treatment of lung diseases."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Pulmonology Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/pulmonology",
    "description": "Best pulmonology hospital in Hyderabad offering asthma treatment, COPD care, bronchoscopy, sleep apnea treatment, tuberculosis care, pulmonary rehabilitation, and respiratory critical care.",
    "keywords": [
      "Best Pulmonologist Hyderabad",
      "Pulmonology Hospital Hyderabad",
      "Asthma Treatment Hyderabad",
      "COPD Treatment Hyderabad",
      "Sleep Apnea Treatment Hyderabad",
      "Bronchoscopy Hyderabad",
      "Pulmonology Hospital Bachupally"
    ]
  }
];

  return (
    <>
  <SEO
    title="Best Pulmonology Hospital in Hyderabad | Lung Specialists | MAMS Hospitals"
    description="MAMS Hospitals offers advanced pulmonology treatment in Hyderabad including asthma treatment, COPD care, bronchoscopy, sleep apnea treatment, pulmonary rehabilitation, and critical respiratory care."
    keywords="best pulmonologist Hyderabad,
pulmonology hospital Hyderabad,
asthma treatment Hyderabad,
COPD treatment Hyderabad,
sleep apnea treatment Hyderabad,
bronchoscopy Hyderabad,
lung specialist Hyderabad,
pulmonary rehabilitation Hyderabad,
tuberculosis treatment Hyderabad,
respiratory care Hyderabad,
PFT test Hyderabad,
pulmonology hospital Bachupally,
MAMS Hospitals Pulmonology"
    schema={schema}
  />
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Best Pulmonology Hospital in Hyderabad
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                Breathe Easy with Expert Lung Care
              </h1>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                MAMS Hospitals offers comprehensive pulmonary care with advanced diagnostics, interventional procedures, 
                and personalized treatment plans for all respiratory conditions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-teal-600 text-white p-6 rounded-2xl shadow-xl z-10">
                <div className="text-3xl font-bold">24/7</div>
                <div className="text-sm">Respiratory Care</div>
              </div>
              <img
                src="/pulmonology-hospital-care.jpg"
                alt="Pulmonology at MAMS Hospital"
                className="rounded-2xl shadow-2xl w-full"
              canonicalUrl="https://mamshospitals.com/departments/pulmonology"
      />
            </div>
          </div>
        </div>
      </section>


      {/* Sub-Specialties Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Sub-Specialties in Pulmonology</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive respiratory expertise across multiple specializations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((specialty, index) => (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-teal-500"
              >
                <CardContent className="p-0">
                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={specialty.image || "/placeholder.svg"}
                      alt={specialty.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{specialty.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{specialty.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

   {/* Treatments & Procedures Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Treatments & Services</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Advanced respiratory treatments and diagnostic services
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {treatments.map((treatment, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                <span className="text-gray-700 font-medium">{treatment}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

         {/* Why Choose Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose MAMS Hospitals for Pulmonology
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Trusted respiratory care with patient-centered approach
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, index) => (
              <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                    <item.icon className="h-7 w-7 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* FAQ Section */}
      {doctors.length > 0 && (
        <section className="py-16 px-0 bg-apple-gray-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialists</h2>
            </div>
            <div className="relative">
              <button
                onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
                {doctors.map(doctor => (
                  <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                    <DoctorCard doctor={{
                      name: doctor.title.rendered,
                      slug: doctor.slug,
                      imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                      specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                      designation: doctor.acf?.designation || "",
                      qualification: doctor.acf?.qualification || "",
                      experience: doctor.acf?.experience || "",
                    }} />
                  </div>
                ))}
              </div>
              <button
                onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
            <div className="text-center mt-8">
              <Link to="/doctors">
                <button className="px-6 py-3 rounded-full border text-sm font-medium inline-flex items-center gap-2">View All Doctors</button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-0 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Get answers to common questions about pulmonology care</p>
          </div>

          <Accordion type="single" collapsible className="bg-white rounded-lg shadow-sm">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left px-6 hover:text-teal-600">{faq.question}</AccordionTrigger>
                <AccordionContent className="px-6 text-gray-600 leading-relaxed">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

   
      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department</p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  )
}
